/******************************************************************************
 *** BgtSpcExpndRsdocMng ** 지부 지출결의서 관리 * 일반 결의서, 무증빙 결의서 작성
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Button from "@/js/common/ui/Button";
import Util from "@/js/common/Util";
import TextBox from "@/js/common/ui/input/TextBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";
import BudgetFile from "../budgetFileBox/BudgetFile";
import { budgetFilesProps } from "@/js/common/types/types";
import Axios from "@/js/common/Axios";
import BgtSpcExpndRsdocList from "./BgtBrExpndRsdocList";
import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "../../common/DialogTitleBar";
import BgtSpcEvdncDialog from "./dialog/BgtBrEvdncDialog";
import BgtSpcEvdncList from "./BgtBrEvdncList";
import AtrzLineBox from "../bgtovrl/dialog/AtrzLineBox";

interface Props {
    systemCode: string; // 특별회계기금 시스템 코드
}

const BgtSpcExpndRsdocMng = ( {systemCode}: Props ) => {
    const userInfo = useRecoilValue(Atom.userInfo);
        const evdncUseYnMap: Record<string, string> = {
        E1: userInfo.sysEvdncE1Yn,
        F1: userInfo.sysEvdncF1Yn,
        G1: userInfo.sysEvdncG1Yn,
    };

    const isEvdncUse = evdncUseYnMap[systemCode] === "Y";
    // 검색 조건
    const [upOgnzCd  , setUpOgnzCd  ] = React.useState<string>(userInfo.upOgnzCd);      // 상위조직코드(본부)
    const [ognzCd    , setOgnzCd    ] = React.useState<string>(userInfo.ognzCd  );      // 조직코드(지부)
    const [acntgYear , setAcntgYear ] = React.useState<string>(Util.getNowAcntgYear()); // 회계연도
    const [searchAtrzSttsCd, setSearchAtrzSttsCd] = React.useState<string>("");         // 진행상태(검색)

    // 지출결의서 내용
    const [spcAcntgExpndDocId, setSpcAcntgExpndDocId] = React.useState<string>(""); // 지출결의서 문서 ID
    const [expndRsdocNo      , setExpndRsdocNo      ] = React.useState<string>(""); // 결의서 문서번호
    const [expndRsdocSeCd    , setExpndRsdocSeCd    ] = React.useState<string>(isEvdncUse ? "" : "01"); // 결의서 구분(일반[01], 무증빙[02], 여입[03], 자료제출[04])
    const [implDt            , setImplDt            ] = React.useState<string>(""); // 행위년월일(집행일자)(YYYYMMDD)
    const [bgtSbjct1stCd     , setBgtSbjct1stCd     ] = React.useState<string>(""); // 1차 코드(관)
    const [bgtSbjct2stCd     , setBgtSbjct2stCd     ] = React.useState<string>(""); // 2차 코드(항)
    const [bgtSbjct3stCd     , setBgtSbjct3stCd     ] = React.useState<string>(""); // 3차 코드(목)
    const [abstrCn           , setAbstrCn           ] = React.useState<string>(""); // 적요
    const [memo              , setMemo              ] = React.useState<string>(""); // 메모
    const [implAmt           , setImplAmt           ] = React.useState<string>(""); // 집행금액
    const [atrzOpnnCn        , setAtrzOpnnCn        ] = React.useState<string>(""); // 반려사유(결재의견)
    const [atrzSttsCd        , setAtrzSttsCd        ] = React.useState<string>("00"); // 결재상태정보
    const [availAmount       , setAvailAmount       ] = React.useState<string>(""); // 관항목의 집행가능금액
    const [listLine          , setListLine          ] = React.useState<any[]>([]);  // 결재 라인 정보
    const [openLine          , setOpenLine          ] = React.useState<boolean>(false); // 기본 펼침/접힘

    const [spcExpndFiles     , setSpcExpndFiles     ] = React.useState<File[]>([]); // 저장할 파일
    const [savedSpcExpndFiles, setSavedSpcExpndFiles] = React.useState<budgetFilesProps[]>([]); // 저장된 파일

    const [callTime, setCallTime] = React.useState<Date>(new Date()); // 검색버튼 트리거용

    // 지출증빙내역서
    const [spcEvdncModalOpen, setSpcEvdncModalOpen] = React.useState<boolean>(false); // 지출증빙내역서 모달 오픈
    const [spcAcntgExpndEvdncId, setSpcAcntgExpndEvdncId] = React.useState<string>(""); // 지출증빙 ID
    const [spcEvdncYn, setSpcEvdncYn] = React.useState<string>("N"); // 지출증빙내역 유무
    const [spcEvdncDelYn, setSpcEvdncDelYn] = React.useState<string>("N"); // 지출증빙내역 삭제 여부
    const [sumUseAmt   , setSumUseAmt   ] = React.useState<string>("0"); // 지출증빙 사용금액 합계

    // 특별회계기금 타이틀 설정
    const [systemName, setSystemName] = React.useState<string>("");
    const [fileSeCd, setFileSeCd] = React.useState("");  // 파일 구분코드

    // 관항목에 따른 집행가능 금액 확인
    React.useEffect(() => {
        if (Util.isEmpty(ognzCd) || Util.isEmpty(implDt)) {
            setAvailAmount("0");
            return;
        }
        if (Util.isEmpty(bgtSbjct1stCd) || Util.isEmpty(bgtSbjct2stCd) || Util.isEmpty(bgtSbjct3stCd)) {
            setAvailAmount("0");
            return;
        }

        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/spc/expnd/select-bgt-br-impl-psblty-amt.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                orgCd        : ognzCd,  // 조직코드
                bgtYear      : Util.getAcntgYear(implDt), // 회계연도
                spcAcntgSeCd : systemCode   , // 특별회계기금 시스템코드
                spcAcntgExpndDocId: spcAcntgExpndDocId, // 지출결의서 문서 ID
                bgtSbjct1stCd: bgtSbjct1stCd, // 관
                bgtSbjct2stCd: bgtSbjct2stCd, // 항
                bgtSbjct3stCd: bgtSbjct3stCd, // 목
            },
            onSuccessFn: (res: any) => {
                setAvailAmount(res.result.item.frmtBlncAmt); // 집행가능 금액
                AppContext.hideSpinner();
            },
        });
    }, [ognzCd, implDt, systemCode, bgtSbjct1stCd, bgtSbjct2stCd, bgtSbjct3stCd, spcAcntgExpndDocId]);


    // 특별회계기금 지출결의서 상세정보 조회
    React.useEffect(() => {
        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/spc/expnd/select-bgt-spc-expnd-rsdoc-dtl.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                spcAcntgExpndDocId: spcAcntgExpndDocId   // 지출결의서 문서 ID
            },
            onSuccessFn: (res: any) => {
                if (Util.isNotEmpty(res.result.item)) {
                    setExpndRsdocSeCd(res.result.item.expndRsdocSeCd ); // 결의서 구분
                    setExpndRsdocNo  (res.result.item.expndRsdocNo   ); // 결의서 구분
                    setImplDt       (res.result.item.implDt          ); // 행위년월일(집행일자)
                    setBgtSbjct1stCd(res.result.item.bgtSbjct1stCd   ); // 1차 코드(관)
                    setBgtSbjct2stCd(res.result.item.bgtSbjct2stCd   ); // 2차 코드(항)
                    setBgtSbjct3stCd(res.result.item.bgtSbjct3stCd   ); // 3차 코드(목)
                    setAbstrCn      (res.result.item.abstrCn         ); // 적요
                    setMemo         (res.result.item.memo            ); // 메모
                    setImplAmt      (res.result.item.implAmt         ); // 집행금액
                    setAtrzOpnnCn   (res.result.item.atrzOpnnCn      ); // 반려사유(결재의견)
                    setAtrzSttsCd   (res.result.item.atrzSttsCd      ); // 결재상태정보
                    setSumUseAmt    (res.result.listSum.sumUseAmt    ); // 지출결의서 집행금액 합계
                    setListLine     (res.result.listLine             ); // 결재 라인리스트

                    setSavedSpcExpndFiles(res.result.listFile); // 첨부파일
                }
                AppContext.hideSpinner();
            },
        });
    }, [spcAcntgExpndDocId, systemCode]);

    // 필수 입력사항 체크
    function checkValue() {
        if (Util.isEmpty(expndRsdocSeCd)) { AppContext.openAlert({ title: "결의서 구분 확인", msg: "지출결의서 구분을 선택하세요." , icon: "warning" }); return false; }
        if (Util.isEmpty(implDt        )) { AppContext.openAlert({ title: "행위년월일 확인" , msg: "행위년월일을 선택하세요."      , icon: "warning" }); return false; }
        if (Util.isEmpty(bgtSbjct1stCd )) { AppContext.openAlert({ title: "예산과목 관 확인", msg: "예산과목 관 항목을 선택하세요.", icon: "warning" }); return false; }
        if (Util.isEmpty(bgtSbjct2stCd )) { AppContext.openAlert({ title: "예산과목 항 확인", msg: "예산과목 항 항목을 선택하세요.", icon: "warning" }); return false; }
        if (Util.isEmpty(bgtSbjct3stCd )) { AppContext.openAlert({ title: "예산과목 목 확인", msg: "예산과목 목 항목을 선택하세요.", icon: "warning" }); return false; }
        if (Number(Util.uncomma(availAmount)) <= 0) {
            { AppContext.openAlert({ title: "집행가능 금액 확인", msg: "집행가능한 금액이 없어서 지출결의서를 작성할 수 없습니다.", icon: "warning" }); return false; }
        }
        return true;
    }

    // 특별회계기금 지출결의서 저장
    function doSave(atrzSttsCd: string) {
        let msgText = "임시저장";
        let cfmMsg  = "";

        // 지출증빙내역서 클릭 - 지출결의서 Insert 후 지출증빙내역서 모달 오픈
        if (atrzSttsCd === "00") {
            if (Util.isEmpty(spcAcntgExpndDocId)) {
                cfmMsg = "지출결의서가 저장되지 않았습니다.\n"
            } else {
                setSpcEvdncModalOpen(true);
                return;
            }
        }

        // 필수입력사항 체크
        const isValidate = checkValue();
        if (!isValidate) return;

        // 작성완료 필수입력 확인
        if (atrzSttsCd === "02") {
            if (Util.isEmpty(implAmt)) { AppContext.openAlert({ title: "금액 확인", msg: "금액을 입력하세요.", icon: "warning" }); return; }
            if (Number(Util.uncomma(availAmount)) - Number(Util.uncomma(implAmt)) < 0) {
                AppContext.openAlert({ title: "집행금액 확인",
                    msg: "집행금액[" + Util.comma(implAmt) + "]이 " + "집행가능금액[" + Util.comma(availAmount) + "]보다 큽니다.", icon: "warning" }); return;
            }
            if (Util.isEmpty(abstrCn)) { AppContext.openAlert({ title: "적요 확인", msg: "적요를 입력하세요.", icon: "warning" }); return; }

            // 일반결의서인 경우 첨부파일 필수
            if (isEvdncUse && expndRsdocSeCd === "01") {
                // 증빙 첨부파일 확인
                if (spcEvdncYn === "N") {   // 지출증빙내역서가 없는 경우
                    if (Util.isEmpty(spcExpndFiles) && savedSpcExpndFiles.length === 0) { AppContext.openAlert({ title: "첨부파일 확인", msg: "지출결의서 첨부파일을 등록하세요", icon: "warning" }); return; }
                }
            }

            msgText = "작성완료";
        }

        AppContext.openAlert({
            title: "지출결의서 확인",
            msg: cfmMsg + "지출결의서를 " + msgText + "하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: msgText,
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/expnd/save-bgt-spc-expnd-rsdoc.do",
                    methodType: "post",
                    isMultipart: true,
                    paramData: {
                        spcAcntgExpndDocId: spcAcntgExpndDocId   , // 지출결의서 문서 ID
                        expndRsdocSeCd    : expndRsdocSeCd       , // 지출결의서 구분코드
                        spcAcntgSeCd      : systemCode           , // 시스템 구분 코드
                        orgCd             : ognzCd               , // 조직코드
                        implDt            : implDt               , // 행위년월일 (YYYYMMDD)
                        acntgYear         : Util.getAcntgYear(implDt), // 회계연도
                        bgtSbjct1stCd     : bgtSbjct1stCd        , // 1차 코드(관)
                        bgtSbjct2stCd     : bgtSbjct2stCd        , // 2차 코드(항)
                        bgtSbjct3stCd     : bgtSbjct3stCd        , // 3차 코드(목)
                        abstrCn           : abstrCn              , // 적요
                        memo              : memo                 , // 메모
                        implAmt           : Util.uncomma(implAmt) === "" ? 0 : Number(Util.uncomma(implAmt)), // 집행금액
                        atrzSttsCd        : atrzSttsCd === "00" ? "01" : atrzSttsCd, // 상태정보
                        budgetFiles       : spcExpndFiles        , // 첨부파일
                        fileSeCd          : fileSeCd             , // 파일구분코드(특별회계기금_지출결의서[B02])
                    },
                    onSuccessFn: (res: any) => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "지출결의서 저장",
                            msg: "지출결의서가 저장되었습니다.",
                            icon: "check"
                        });

                        // 지출결의서 Insert 후 문서 ID 설정
                        if (Util.isEmpty(spcAcntgExpndDocId)) {
                            setSpcAcntgExpndDocId(res.result.spcAcntgExpndDocId); // 지출결의서 문서 ID
                            if (atrzSttsCd === "00") setSpcEvdncModalOpen(true); // 지출증빙내역서 모달 오픈
                        }
                        setSpcEvdncDelYn("N");
                        if (atrzSttsCd === "02") {
                             doReset(); // 작성완료인 경우 초기화 처리
                        }else {
                            setSpcExpndFiles([]);
                        }
                        setCallTime(new Date());
                    },
                });
            },
        });
    }


    // 초기화
    function doReset() {
        // 좌측 입력폼
        setSpcAcntgExpndDocId(""); // 지출결의서 문서번호
        setExpndRsdocNo(""); // 지출결의서 문서번호
        setExpndRsdocSeCd    (isEvdncUse ? "" : "01"); // 지출결의서 구분코드
        setImplDt            (""); // 행위년월일 (YYYYMMDD)
        setAbstrCn           (""); // 적요
        setMemo              (""); // 메모
        setImplAmt           (""); // 집행금액
        setAtrzSttsCd        ("00"); // 상태정보
        setAvailAmount       ("0"); // 집행가능금액

        // 첨부파일
        setSpcExpndFiles     ([]); // 저장할 파일
        setSavedSpcExpndFiles([]); // 저장된 파일
    }

    // 문서 ID 변경 시 드롭존 초기화
    React.useEffect(() => {
        setSpcExpndFiles([]);
    }, [spcAcntgExpndDocId]);

    // 특별회계기금 지출결의서 삭제
    function doDelete() {
        AppContext.openAlert({
            title: "확인",
            msg: "지출결의서를 삭제하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "삭제",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/expnd/delete-bgt-spc-expnd-rsdoc.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        spcAcntgExpndDocId: spcAcntgExpndDocId, // 지출결의서 문서 ID
                    },
                    onSuccessFn: () => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "지출결의서 삭제",
                            msg: "지출결의서가 삭제되었습니다.",
                            icon: "check"
                        });
                        doReset(); // 초기화
                        setCallTime(new Date());
                    },
                });
            },
        });
    }

    // 특별회계기금 타이틀 설정
    React.useEffect(() => {
        if (systemCode === "E1") {
            setSystemName("지부 운영비");
            setFileSeCd("E01");
            setBgtSbjct1stCd("E_06_001");
            setBgtSbjct2stCd("E_06_001_001");
            setBgtSbjct3stCd("E_06_001_001_001");
        } else if (systemCode === "F1") {
            setSystemName("지부장 직무활동비");
            setFileSeCd("F01");
            setBgtSbjct1stCd("E_07_001");
            setBgtSbjct2stCd("E_07_001_001");
            setBgtSbjct3stCd("E_07_001_001_001");
        } else if (systemCode === "G1") {
            setSystemName("지부 대회비");
            setFileSeCd("G01");
            setBgtSbjct1stCd("E_08_001");
            setBgtSbjct2stCd("E_08_001_001");
            setBgtSbjct3stCd("E_08_001_001_001");
        }
    }, [systemCode]);

    // 첨부파일 삭제할 경우 상태정보 변경
    React.useEffect(() => {
        if (atrzSttsCd === "00" || atrzSttsCd === "01") return;

        // 일반 지출결의서
        if (expndRsdocSeCd === "01" && atrzSttsCd === "02") {
            if (spcEvdncYn === "N" && savedSpcExpndFiles.length === 0) {
                setAtrzSttsCd("01");
                // 상태정보 업데이트 처리
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/expnd/update-bgt-spc-expnd-stts-cd.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        spcAcntgExpndDocId: spcAcntgExpndDocId,
                        atrzSttsCd: "01"
                    },
                    onSuccessFn: () => {
                        AppContext.hideSpinner();
                        setCallTime(new Date());
                    },
                });
            }
        }
    }, [atrzSttsCd, expndRsdocSeCd, spcEvdncYn, savedSpcExpndFiles, spcAcntgExpndDocId]);

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">{systemName} 지출결의서 관리
                    <span className={`w100p fm-b fs16 ml10 ${isEvdncUse ? "fc007CE8" : "fcC0392B"}`}>{isEvdncUse ? "(증빙첨부 필수)" : "(증빙첨부 미사용)"}</span>
                </span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 조건 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    {/* 소속 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox value={upOgnzCd}
                            width={150} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#f6f6f6" paddingRight={8}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox value={ognzCd}
                            width={180} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox value={acntgYear}
                            width={120} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    {/* 진행상태 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">진행상태</span>
                        <SelectBox  value={searchAtrzSttsCd}
                            width={140} height={36}
                            fontSize={14}
                            fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff"
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("UI_ATRZ_STTS_CD_01"))}
                            placeholder="전체" paddingRight={8}
                            onChangeFunc={(data: string) => setSearchAtrzSttsCd(data)}
                        />
                    </div>
                </div>
            </div>

            {/* 좌/우 */}
            <div className="dpFlx gap14 aiFs">
                {/* LEFT */}
                <div className="w360">
                    <div className="bd1d9d9d9 bdrd6 bgcWhite p15">
                        <div className="mb10">
                            <span className="fm-b fs16 fc007bff ls1 fw700">지출결의서 입력</span>
                            <span className="fm-b fs13 ls06 fc1A5276" style={{marginLeft: "66px"}}>{expndRsdocNo}</span>
                        </div>

                        {atrzSttsCd === "05" && (
                        <AtrzLineBox
                            listLine={listLine}
                            open={openLine}
                            onToggle={() => setOpenLine((v) => !v)}
                        />)}

                        {/* 결의서 구분 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">결의서 구분</span>
                            </div>
                            <div className="w320 h36 bd1d9d9d9 dpFlx aiC bsBdb" style={{backgroundColor: ["01", "02", "05"].includes(atrzSttsCd) || !isEvdncUse ? "#F6F6F6": "white"}}>
                                <div className="ml10">
                                    <label className="dpFlx csP">
                                        <input type="radio" value={"01"} checked={expndRsdocSeCd === "01"}
                                            onChange={(e) => setExpndRsdocSeCd(e.target.value)}
                                            style={{transform: "scale(1.2)"}}
                                            disabled={["01", "02", "05"].includes(atrzSttsCd) || !isEvdncUse ? true : false}   // 작성중(01), 작성완료(02), 반려(05): 임시저장 이후 수정불가
                                        />
                                        <div className="mb2">
                                            <span className="fm-b c333333 ls1 fs14 ml3" style={{fontWeight: expndRsdocSeCd === "01" ? "700" : ""}}>일반 지출결의서</span>
                                        </div>
                                    </label>
                                </div>
                                <div className="ml10 ml20 aiC">
                                    <label className="dpFlx csP">
                                        <input type="radio" value={"02"} checked={expndRsdocSeCd === "02"}
                                            onChange={(e) => setExpndRsdocSeCd(e.target.value)}
                                            style={{transform: "scale(1.2)"}}
                                            disabled={["01", "02", "05"].includes(atrzSttsCd) || !isEvdncUse ? true : false}   // 작성중(01), 작성완료(02), 반려(05): 임시저장 이후 수정불가
                                        />
                                        <div className="mb2">
                                            <span className="fm-b c333333 ls1 fs14 ml3" style={{fontWeight: expndRsdocSeCd === "02" ? "700" : ""}}>무증빙 지출결의서</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* 행위년월일 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">행위년월일</span>
                            </div>
                            <DatePickerBox value={implDt}
                                width={320} height={36} fontSize={14}
                                fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                bgColor={["01", "02", "05"].includes(atrzSttsCd) ? "#F6F6F6": "white"}
                                textAlign="left"
                                calendarThemeColor={AppContext.themeColor}
                                disabled={["01", "02", "05"].includes(atrzSttsCd) ? true : false}   // 작성중(01), 작성완료(02), 반려(05): 임시저장 이후 수정불가
                                onChange={(data: string) => setImplDt(data)}
                            />
                        </div>

                        {/* 관/항/목 */}
                        <div className="dpFlx mb10">
                            {/* 관 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">관</span>
                                </div>
                                <SelectBox value={bgtSbjct1stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#f6f6f6" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt1CodeArray(systemCode, "02"))}
                                    placeholder="선택"
                                    disabled={true}
                                    onChangeFunc={(data: string) => setBgtSbjct1stCd(data)}
                                />
                            </div>

                            {/* 항 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">항</span>
                                </div>
                                <SelectBox value={bgtSbjct2stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#f6f6f6" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt2CodeArray(systemCode, bgtSbjct1stCd))}
                                    placeholder="선택"
                                    disabled={true}
                                    onChangeFunc={(data: string) => setBgtSbjct2stCd(data)}
                                />
                            </div>

                            {/* 목 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">목</span>
                                </div>
                                <SelectBox  value={bgtSbjct3stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#f6f6f6" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt3CodeArray(systemCode, bgtSbjct2stCd))}
                                    placeholder="선택"
                                    disabled={true}
                                    onChangeFunc={(data: string) => setBgtSbjct3stCd(data)}
                                />
                            </div>
                        </div>

                        {/* 금액 + 집행 가능 금액 */}
                        <div className="dpFlx gap10 aiE mb10">
                            {/* 금액 */}
                            <div className="flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">금액 (원)</span>
                                </div>
                                <div className="w155 h36 dpFlx aiC bsBdb">
                                    <NumberTextBox value={implAmt}
                                        className={spcEvdncYn === "Y" ? "bgcF0F0F0 w100p h36 bd1 bdrd2" : "w100p h36 bd1 bdrd2"}
                                        thousandsGroupStyle="thousand" thousandSeparator=","
                                        placeholder="0"
                                        fontSize={14} fontFamily="fm-c" letterSpacing={-1}
                                        onChange={(data: string) => setImplAmt(data)}
                                        inputClass={"taR mr10"}
                                        color="#007bff"
                                        disabled={spcEvdncYn === "Y" ? true : false}
                                    />
                                </div>
                            </div>

                            {/* 집행 가능 금액 */}
                            <div className="flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">집행 가능 금액</span>
                                </div>
                                <div className="w155 h36 dpFlx aiC bsBdb">
                                    <NumberTextBox  value={availAmount}
                                        className="w100p h100p bd1 bdrd2 bgcF0F0F0"
                                        thousandsGroupStyle="thousand" thousandSeparator=","
                                        placeholder="0"
                                        fontSize={14} fontFamily="fm-c" letterSpacing={-1} color="#333"
                                        inputClass={"taR mr10"}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 적요 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">적요</span>
                            </div>
                            <TextBox value={abstrCn}
                                width={320} height={36} fontSize={14}
                                fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                placeholder="예) 12월 소모품 구입"
                                onChangeFunc={(text: string) => setAbstrCn(text)}
                            />
                        </div>

                        {/* 메모 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">메모</span>
                            </div>
                            <TextBox value={memo}
                                width={320} height={36} fontSize={14}
                                fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                placeholder="메모를 입력하세요."
                                onChangeFunc={(text: string) => setMemo(text)}
                            />
                        </div>

                        {/* ++++++++++++++++++++ 지출 증빙내역서 목록 +++++++++++++++++++++ */}
                        <BgtSpcEvdncList
                            spcAcntgExpndDocId      = {spcAcntgExpndDocId}      // 지출결의서 문서 ID
                            doSave                  = {doSave}                  // 지출결의서 저장
                            setSpcEvdncModalOpen    = {setSpcEvdncModalOpen}    // 지출증빙내역서 모달 오픈
                            setSpcAcntgExpndEvdncId = {setSpcAcntgExpndEvdncId} // 지출증빙내역서 ID
                            setSpcEvdncYn           = {setSpcEvdncYn}           // 지출증빙내역서 유무
                            setImplAmt              = {setImplAmt}              // 지출결의서 집행금액 셋팅
                            spcEvdncDelYn           = {spcEvdncDelYn}           // 지출증빙내역서 삭제 여부
                            callTime                = {callTime}
                        ></BgtSpcEvdncList>

                        {/* 반려 */}
                        {atrzSttsCd === "05" &&
                            <div className="dpFlx flxdCol">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">반려</span>
                                </div>
                                <div className="dpFlx aiFs">
                                    <div className="flxg1 bsBdb">
                                        <textarea
                                            value={atrzOpnnCn}
                                            className="w320 h70 bsBdb p10 bd1 bdcDFDFDF bdrd2 fm-c fs14 fc333333 ls1 bgcWhite rszN"
                                            maxLength={2000}
                                            placeholder=""
                                            onChange={(e) => setAtrzOpnnCn(e.target.value)}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        }

                        {/* 첨부파일 */}
                        <BudgetFile
                            atrzSttsCd          = {atrzSttsCd}     // 결재상태코드
                            fileSeCd            = {fileSeCd}              // 파일구분코드(특별회계기금_지출결의서[B02])
                            savedBudgetFiles    = {savedSpcExpndFiles}
                            setSavedBudgetFiles = {setSavedSpcExpndFiles}
                            budgetFiles         = {spcExpndFiles}
                            setBudgetFiles      = {setSpcExpndFiles}
                            height              = "100px"
                        />

                        <div className="bdt1 bdcDDDDDD mt15 mb15" />

                        {/* 버튼 4개 */}
                        <div className="dpFlx jcSb aiC gap10">
                            <Button buttonName="초기화"
                                width={80} height={40}
                                fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                variant="styInitBtn" borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={() => doReset()}
                            />

                            {(atrzSttsCd === "01" || atrzSttsCd === "02" || atrzSttsCd === "05") &&
                                <Button buttonName="삭제"
                                    width={75} height={40}
                                    fontFamily="fm-b" fontSize={15} letterSpacing={-1}
                                    variant="styDeleteBtn" borderRadius={6}
                                    buttonClass="dpFlx aiC jcC"
                                    onClickFunc={doDelete}
                                />
                            }

                            {(atrzSttsCd === "00" || atrzSttsCd === "01") &&
                               <Button buttonName="임시저장"
                                    width={100} height={40}
                                    fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                    variant="styTempBtn" borderRadius={6}
                                    buttonClass="dpFlx aiC jcC"
                                    onClickFunc={() => doSave("01")}
                                />
                            }

                            <Button buttonName="작성완료"
                                width={100} height={40}
                                fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                variant="styCompleteBtn" borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={() => doSave("02")}
                            />
                        </div>
                    </div>
                </div>

                {/* RIGHT 지출결의서 목록*/}
                <div className="flx1">
                    <BgtSpcExpndRsdocList
                        ognzCd={ognzCd}
                        acntgYear={acntgYear}
                        systemCode={systemCode}
                        searchAtrzSttsCd={searchAtrzSttsCd}
                        setSpcAcntgExpndDocId={setSpcAcntgExpndDocId}
                        prcsExpndRsdocSeCd = "01"   // 결의서 구분 검색 조건(일반, 무증빙)
                        callTime={callTime}
                    ></BgtSpcExpndRsdocList>
                </div>
            </div>
            {/* 좌/우 끝 */}

            {/* 지출증빙내역서 모달 */}
            <Modal modalOpen={spcEvdncModalOpen} dim={true} visible={true}
                titleEl={<DialogTitleBar title="지출증빙내역서" backgroundColor={AppContext.themeColor} />}
                onCloseFunc={() => {
                    setSpcAcntgExpndEvdncId("");
                    setSpcEvdncModalOpen(false);
                }}
            >
                <BgtSpcEvdncDialog
                    spcAcntgExpndDocId      = {spcAcntgExpndDocId}         // 지출결의서 문서 ID
                    spcAcntgExpndEvdncId    = {spcAcntgExpndEvdncId}       // 지출증빙내역서 ID
                    setSpcAcntgExpndEvdncId = {setSpcAcntgExpndEvdncId}    // 지출증빙내역서 ID 셋팅
                    acntgYear               = {Util.getAcntgYear(implDt)}  // 회계연도
                    setSpcEvdncModalOpen    = {setSpcEvdncModalOpen}
                    setSpcEvdncDelYn        = {setSpcEvdncDelYn}           // 지출증빙내역서 삭제 여부
                    availAmount             = {availAmount}                // 집행가능금액
                    sumUseAmt               = {sumUseAmt}                  // 지출증빙내역서 사용금액 합계
                    isEvdncUse              = {isEvdncUse}                 // 증빙첨부 사용여부
                    setCallTime             = {setCallTime}
                />
            </Modal>
        </div>
    );
};

export default BgtSpcExpndRsdocMng;
